import React, { useState } from "react";
import { Search, ShieldCheck, Zap, Lock, Mail, Linkedin, Sparkles, AlertTriangle, CheckCircle2, ScanLine } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * <HullScanner /> — public B2B conversion widget for the homepage.
 *
 * Step 1: visitor enters URL → real scan
 * Step 2: snippet HUD shows Performance + Security scores live; Intelligence + findings paywalled
 * Step 3: blurred watermarked preview + two CTAs
 *   (a) Free · "Request a Sovereign Review" (email + LinkedIn capture)
 *   (b) Paid · "Unlock Full Forensic Report" — $49 Strike Fee → 60/30/10 covenant
 */
export default function HullScanner() {
    const [url, setUrl] = useState("");
    const [scanning, setScanning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    // Modal state
    const [modal, setModal] = useState(null);  // 'review' | 'unlock' | null
    const [reviewForm, setReviewForm] = useState({ email: "", linkedin_url: "", name: "", company: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    const runScan = async (e) => {
        if (e) e.preventDefault();
        if (!url.trim()) return;
        setScanning(true); setError(""); setResult(null);
        try {
            const { data } = await api.post("/hull-scan", { url: url.trim() });
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.detail || "Scan failed.");
        } finally {
            setScanning(false);
        }
    };

    const submitReview = async () => {
        if (!reviewForm.email || !result?.scan_id) return;
        setSubmitting(true);
        try {
            await api.post(`/hull-scan/${result.scan_id}/sovereign-review`, reviewForm);
            toast.success("Review requested · A Sovereign Operative will reach you within 24h.");
            setModal(null);
            setReviewForm({ email: "", linkedin_url: "", name: "", company: "", notes: "" });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const startUnlock = async () => {
        if (!result?.scan_id) return;
        setSubmitting(true);
        try {
            const { data } = await api.post(`/hull-scan/${result.scan_id}/checkout`, {
                origin_url: window.location.origin,
                email: reviewForm.email || undefined,
            });
            if (data.url) {
                window.location.href = data.url;
            } else if (data.already_unlocked) {
                window.location.href = `/hull-scan/unlocked/${result.scan_id}`;
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Checkout failed.");
            setSubmitting(false);
        }
    };

    return (
        <section
            className="rounded-3xl border border-[hsl(40_50%_35%/0.4)] bg-gradient-to-br from-[hsl(151_55%_8%)] via-[hsl(151_50%_10%)] to-[hsl(40_30%_8%)] p-6 sm:p-10 relative overflow-hidden"
            data-testid="hull-scanner-widget"
        >
            {/* Starfield bg flourish */}
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ background: "radial-gradient(circle at 30% 20%, hsl(45 85% 60%) 0px, transparent 90px), radial-gradient(circle at 80% 80%, hsl(195 70% 60%) 0px, transparent 110px)" }} />
            <div className="relative">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_70%)]">
                    <ScanLine size={12} /> Platform Scouter · Sovereign Diagnostic
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl mt-2 tracking-tighter">
                    Scan your hull for holes.
                </h2>
                <p className="text-muted-foreground mt-2 max-w-2xl text-sm">
                    Drop your URL. EIGOO runs a live forensic across Performance, Security, and Intelligence.
                    The snippet HUD is free. The full forensic is gated behind a $49 Delivery Fee — or a free Sovereign Review.
                </p>

                {/* Input */}
                <form onSubmit={runScan} className="mt-6 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                        <input
                            type="text"
                            placeholder="https://your-website.com"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            data-testid="hull-scanner-input"
                            className="w-full pl-10 pr-3 py-3 rounded-md bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.4)] text-sm focus:outline-none focus:border-[hsl(45_85%_62%)]"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={scanning || !url.trim()}
                        data-testid="hull-scanner-submit"
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-sm uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                    >
                        {scanning ? <><Sparkles size={14} className="animate-pulse" /> Scanning…</> : <><Zap size={14} /> Run Platform Scan</>}
                    </button>
                </form>
                {error && <div className="mt-3 text-sm text-[hsl(350_75%_72%)]" data-testid="hull-scanner-error">⚠ {error}</div>}

                {/* Snippet HUD */}
                {result && (
                    <div className="mt-8" data-testid="hull-scanner-result">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                            Snippet HUD · {result.summary?.host} · {result.summary?.elapsed_ms}ms · {result.findings_count} finding{result.findings_count === 1 ? "" : "s"} detected
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                            <ScoreCard label="Performance" value={result.scores_revealed?.performance ?? 0} accent="hsl(45 85% 65%)" icon={Zap} testid="hull-score-performance" />
                            <ScoreCard label="Security" value={result.scores_revealed?.security ?? 0} accent="hsl(151 60% 60%)" icon={ShieldCheck} testid="hull-score-security" />
                            <LockedCard label="Intelligence" />
                        </div>

                        {/* Findings teaser - blurred */}
                        <div className="mt-6 relative rounded-xl border border-[hsl(40_50%_35%/0.3)] bg-[hsl(151_55%_8%/0.6)] overflow-hidden" data-testid="hull-vulnerability-brief">
                            <div className="px-5 py-3 border-b border-[hsl(40_30%_25%/0.3)] flex items-center justify-between">
                                <div className="font-heading text-sm flex items-center gap-2"><AlertTriangle size={14} className="text-[hsl(45_85%_65%)]" /> Vulnerability Brief</div>
                                <span className="text-[10px] uppercase tracking-wider text-[hsl(45_85%_70%)] flex items-center gap-1"><Lock size={10} /> Watermarked Preview</span>
                            </div>
                            <div className="relative">
                                <div className="space-y-2 p-5 select-none" style={{ filter: "blur(4px)", pointerEvents: "none" }} aria-hidden="true">
                                    {(result.findings_teaser || []).map((f, i) => (
                                        <div key={i} className="flex items-start gap-3 text-xs">
                                            <span className={`shrink-0 mt-0.5 px-2 py-0.5 rounded-full uppercase tracking-wider text-[9px] ${
                                                f.severity === "critical" ? "bg-[hsl(350_55%_25%/0.6)] text-[hsl(350_75%_75%)]" :
                                                f.severity === "warning" ? "bg-[hsl(45_50%_22%/0.6)] text-[hsl(45_85%_72%)]" :
                                                "bg-[hsl(195_50%_18%/0.5)] text-[hsl(195_70%_70%)]"
                                            }`}>{f.severity}</span>
                                            <div>
                                                <div className="font-semibold">{f.label}</div>
                                                <div className="text-muted-foreground">{f.message}</div>
                                            </div>
                                        </div>
                                    ))}
                                    {/* extra blurred filler so it looks substantive */}
                                    {[0,1,2,3].map((i) => (
                                        <div key={`f-${i}`} className="flex items-start gap-3 text-xs">
                                            <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-full bg-[hsl(45_50%_22%/0.4)] text-[hsl(45_85%_72%)] uppercase tracking-wider text-[9px]">██████</span>
                                            <div className="flex-1">
                                                <div className="font-semibold">█████████ █████ ████ ███████</div>
                                                <div className="text-muted-foreground">██████████ █████████ ██████ ██████ ████ ████████ ████████ ████ ███████.</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {/* EIGOO watermark overlay */}
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="rotate-[-12deg] text-[hsl(45_85%_70%)] opacity-20 font-heading text-7xl tracking-tighter select-none">EIGOO LOCKED</div>
                                </div>
                            </div>
                        </div>

                        {/* Two CTAs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            <button
                                onClick={() => setModal("review")}
                                data-testid="hull-cta-review"
                                className="group rounded-xl border border-[hsl(151_55%_30%/0.5)] bg-[hsl(151_55%_10%/0.6)] hover:bg-[hsl(151_55%_15%/0.8)] hover:border-[hsl(151_60%_50%)] p-5 text-left transition-colors"
                            >
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(151_60%_70%)]">
                                    <Mail size={11} /> Free Path
                                </div>
                                <div className="font-heading text-xl mt-2">Request a Sovereign Review</div>
                                <div className="text-xs text-muted-foreground mt-2">
                                    A Sovereign Operative reviews your scan and emails the brief within 24h. Gives EIGOO your contact + LinkedIn so we can route you to the right Privateer.
                                </div>
                            </button>
                            <button
                                onClick={() => setModal("unlock")}
                                data-testid="hull-cta-unlock"
                                className="group rounded-xl border-2 border-[hsl(45_75%_55%/0.5)] bg-gradient-to-br from-[hsl(40_50%_15%/0.6)] to-[hsl(45_60%_10%/0.6)] hover:border-[hsl(45_85%_65%)] p-5 text-left transition-colors"
                            >
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">
                                    <Sparkles size={11} /> $49 Delivery Fee · 60/30/10 Covenant
                                </div>
                                <div className="font-heading text-xl mt-2">Unlock Full Forensic Report</div>
                                <div className="text-xs text-muted-foreground mt-2">
                                    Instant access to all findings, the Intelligence score, and the sealed JSON forensic. Stripe checkout · Delivery Fee splits 60% Commander · 30% Fleet · 10% CyberKids Anchor.
                                </div>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Free path modal */}
            {modal === "review" && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="hull-review-modal">
                    <div className="bg-[hsl(151_55%_8%)] border border-[hsl(40_50%_35%/0.5)] rounded-2xl max-w-md w-full p-6">
                        <div className="font-heading text-2xl">Request a Sovereign Review</div>
                        <div className="text-xs text-muted-foreground mt-1">Free · 24h turnaround · zero spam.</div>
                        <div className="space-y-3 mt-5">
                            <Field label="Email *" type="email" value={reviewForm.email} onChange={(v) => setReviewForm({...reviewForm, email: v})} testid="hull-review-email" />
                            <Field label="LinkedIn URL" value={reviewForm.linkedin_url} onChange={(v) => setReviewForm({...reviewForm, linkedin_url: v})} placeholder="https://linkedin.com/in/..." testid="hull-review-linkedin" icon={Linkedin} />
                            <Field label="Your name" value={reviewForm.name} onChange={(v) => setReviewForm({...reviewForm, name: v})} testid="hull-review-name" />
                            <Field label="Company" value={reviewForm.company} onChange={(v) => setReviewForm({...reviewForm, company: v})} testid="hull-review-company" />
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button onClick={() => setModal(null)} className="px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_15%)]">Cancel</button>
                            <button
                                onClick={submitReview}
                                disabled={submitting || !reviewForm.email}
                                data-testid="hull-review-submit"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(151_60%_30%)] text-[hsl(151_30%_95%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(151_65%_38%)] disabled:opacity-40"
                            >
                                <CheckCircle2 size={13} /> {submitting ? "Sending…" : "Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Paid path modal */}
            {modal === "unlock" && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="hull-unlock-modal">
                    <div className="bg-[hsl(151_55%_8%)] border-2 border-[hsl(45_75%_55%/0.5)] rounded-2xl max-w-md w-full p-6">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">$49 Delivery Fee · 60/30/10 Covenant</div>
                        <div className="font-heading text-2xl mt-1">Unlock Full Forensic Report</div>
                        <div className="text-xs text-muted-foreground mt-2">
                            Instant Stripe checkout. Upon capture: 60% routes to the closing Commander, 30% to Fleet ops, 10% to the CyberKids Anchor — the LAW.
                        </div>
                        <div className="mt-4 rounded-md border border-[hsl(40_30%_25%/0.4)] bg-[hsl(151_55%_6%)] p-3 text-xs space-y-1.5">
                            <Row label="Delivery Fee" value="$49.00" />
                            <Row label="Commander (60%)" value="$29.40" accent="hsl(45 85% 70%)" />
                            <Row label="Fleet (30%)" value="$14.70" accent="hsl(195 70% 65%)" />
                            <Row label="CyberKids Anchor (10%)" value="$4.90" accent="hsl(151 60% 65%)" />
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button onClick={() => setModal(null)} className="px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_15%)]">Cancel</button>
                            <button
                                onClick={startUnlock}
                                disabled={submitting}
                                data-testid="hull-unlock-pay"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] disabled:opacity-40"
                            >
                                <Sparkles size={13} /> {submitting ? "Loading Stripe…" : "Continue to Stripe"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function ScoreCard({ label, value, accent, icon: Icon, testid }) {
    const grade = value >= 80 ? "A" : value >= 60 ? "B" : value >= 40 ? "C" : "F";
    const ring = (value / 100) * 100;
    return (
        <div className="rounded-xl border border-[hsl(40_50%_35%/0.3)] bg-[hsl(151_55%_10%/0.6)] p-4" data-testid={testid}>
            <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: accent + "22", color: accent }}>
                    <Icon size={14} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
                    <div className="text-[11px] text-foreground/70">Grade {grade}</div>
                </div>
            </div>
            <div className="font-heading text-4xl" style={{ color: accent }}>{value}<span className="text-base text-muted-foreground">/100</span></div>
            <div className="mt-2 h-1.5 rounded-full bg-[hsl(40_30%_20%)] overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${ring}%`, background: accent }} />
            </div>
        </div>
    );
}

function LockedCard({ label }) {
    return (
        <div className="rounded-xl border-2 border-dashed border-[hsl(45_70%_50%/0.4)] bg-[hsl(40_30%_8%/0.5)] p-4 relative overflow-hidden" data-testid="hull-score-intelligence-locked">
            <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-md flex items-center justify-center bg-[hsl(45_70%_15%)] text-[hsl(45_85%_65%)]">
                    <Lock size={14} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
                    <div className="text-[11px] text-[hsl(45_85%_70%)]">Locked</div>
                </div>
            </div>
            <div className="font-heading text-4xl text-[hsl(45_85%_70%)] opacity-40 select-none filter blur-sm">██<span className="text-base">/100</span></div>
            <div className="mt-2 text-[10px] text-muted-foreground">Unlock to reveal · score + recommendations</div>
        </div>
    );
}

function Field({ label, type = "text", value, onChange, placeholder, testid, icon: Icon }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-1.5">{label}</label>
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={13} />}
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    className={`w-full ${Icon ? "pl-9" : "pl-3"} pr-3 py-2 rounded-md bg-[hsl(151_55%_6%)] border border-[hsl(40_40%_30%/0.4)] text-sm focus:outline-none focus:border-[hsl(45_85%_62%)]`}
                />
            </div>
        </div>
    );
}

function Row({ label, value, accent }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-mono font-semibold" style={accent ? { color: accent } : {}}>{value}</span>
        </div>
    );
}
